import {React , useState, useEffect} from 'react';
import Select from "react-select";
import { Users } from 'lucide-react';
import { toast } from 'sonner'; 
import { BASE_URL } from '../apiConfig';
import { useAuth } from "../context/AuthProvider";
import { useLanguage } from "../context/LanguageProvider";

export default function ConfirmatorSellers({  
  sellerOptions,
  selectedSellers,
  setSelectedSellers,
  sellersData,
  currentUserData
}) {
  const { t } = useLanguage();

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { token, role, roles } = useAuth();
  const [initialSellers, setInitialSellers] = useState([]);

  useEffect(() => {
    if (currentUserData?.info?.sellers && currentUserData.info.sellers.length > 0) {
      const sellerIds = currentUserData.info.sellers.map(seller => seller.id);
      setSelectedSellers(sellerIds);
      setInitialSellers(sellerIds);
    } else {
      setSelectedSellers([]);
      setInitialSellers([]);
    }
  }, [currentUserData, setSelectedSellers]);

  const selectedSellerOptions = sellerOptions.filter(option => 
    selectedSellers.includes(option.value)
  );

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/confirmatorsellers/${currentUserData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          seller_ids: selectedSellers
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (data.errors) {
          setErrors(data.errors);
        }
        throw new Error(data.message || t('cors_select_sallers.update_error'));
      }
      
      setIsEditing(false);
      setInitialSellers(selectedSellers);
      setErrors({});
      toast.success(t('cors_select_sallers.update_success'));
    } catch (error) {
      toast.error(error.message || t('cors_select_sallers.update_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedSellers([...initialSellers]);
    setIsEditing(false);
    setErrors({});
  };

  const hasChanges = () => {
    if (selectedSellers.length !== initialSellers.length) return true;
    
    const sortedCurrent = [...selectedSellers].sort();
    const sortedInitial = [...initialSellers].sort();
    
    return !sortedCurrent.every((value, index) => value === sortedInitial[index]);
  };

  return (
     <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mt-8">
       <div className="px-8 max-xl:px-5">
        
        <div className="space-y-3 mt-6">
          {
            (role === 1 || (role === 2 && roles.edit_confirmator === 1)) &&
              <div className="flex space-x-3 justify-end" >
                {isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isLoading || !hasChanges()}
                    className={`bg-[var(--color-primary-500)] text-white cursor-pointer px-6 py-2 rounded-lg font-semibold hover:bg-[var(--color-primary-400)] transition-colors duration-200 flex items-center space-x-2 ${
                      isLoading || !hasChanges() ? 'opacity-70' : ''
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="max-xl:text-xs">{t('cors_select_sallers.updating')}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm max-xl:text-xs">{t('cors_select_sallers.save')}</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="bg-red-100 text-red-600 cursor-pointer px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-sm max-xl:text-xs">{t('cors_select_sallers.cancel')}</span>
                    </button>
                
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="bg-[var(--color-primary-50)] text-[var(--color-primary-500)] cursor-pointer px-4 py-2 rounded-lg font-semibold transition-colors duration-200 flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  <span className="text-sm max-xl:text-xs">{t('cors_select_sallers.edit_sellers')}</span>
                </button>
              )}
              </div>
          }
          <div>
            <div className="flex items-center mb-4">
              <div className="bg-[var(--color-primary-50)] p-3 rounded-lg mr-4">
                <Users  className="w-6 h-6 text-[var(--color-primary-500)]" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">{t('cors_select_sallers.title')}</h2>
            </div>
            <p className="text-gray-600 mb-4 text-xs">{t('cors_select_sallers.description')}</p>
          </div>
        
        </div>
      </div>
      <div className="px-6 pb-6">
        
        <div className="mt-4">
          {isEditing ? (
            <>
              <Select
                isMulti
                options={sellerOptions}
                value={selectedSellerOptions}
                onChange={(selectedOptions) => {
                  setSelectedSellers(selectedOptions.map(option => option.value));
                }}
                className={`w-full py-0.5 px-1 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]`}
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    border: "none",
                    cursor: "pointer",
                  }),
                }}
                placeholder={t('cors_select_sallers.search_placeholder')}
                noOptionsMessage={() => t('cors_select_sallers.no_sellers_found')}
                isSearchable
              />

              {errors.seller_ids && <p className="mt-2 text-red-600 text-xs">{errors.seller_ids[0]}</p>}
            </>
          ) : null}

          {selectedSellers.length > 0 ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {sellersData
                .filter(seller => selectedSellers.includes(seller.id))
                .map(seller => (
                  <div
                    key={seller.id}
                    className="flex items-center text-[var(--color-primary-500)] bg-[var(--color-primary-50)] max-h-max border border-[var(--color-primary-500)] rounded-full px-3 text-[11px]"
                  >
                    <span>
                      {seller.name} ({seller.name_store})
                    </span>
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => setSelectedSellers(prev => prev.filter(id => id !== seller.id))}
                        className="ml-2 text-lg text-[var(--color-primary-500)] hover:text-[var(--color-primary-50)]0 cursor-pointer "
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-gray-400 text-xs mt-2">{t('cors_select_sallers.no_sellers_selected')}</p>
          )}
        </div>
      </div>
    </div>
  )
}